import { useMutation, useQueryClient } from '@tanstack/react-query'

import { useSnackbar } from '@/hooks/useSnackbar'
import { DisableStaff, EnableStaff } from '@/services/StaffService'

interface SetStaffEnabledVariables {
  orgId: string
  staffId: string
  enabled: boolean
  /** Reported once the change lands, so the wording stays with the tab that owns it. */
  successMessage: string
}

/**
 * Switch one staff member's Barti access on or off.
 *
 * One hook rather than two: both directions take the same variables, refresh the
 * same queries, and report themselves the same way, so the direction is a value
 * rather than a second mutation.
 *
 * Both reports are made here rather than by the caller. A caller that waited on
 * the call to report it would lose the report if a second change started first,
 * and there is nothing a caller could add to a refusal anyway — the endpoint
 * answers one with the state it left the login in and whether a retry is worth
 * trying, which the global toast already carries.
 */
export const useSetStaffEnabled = () => {
  const queryClient = useQueryClient()
  const { showSuccess } = useSnackbar()

  return useMutation({
    mutationFn: ({ orgId, staffId, enabled }: SetStaffEnabledVariables) =>
      enabled ? EnableStaff(orgId, staffId) : DisableStaff(orgId, staffId),
    onSuccess: (_staff, { orgId, successMessage }) => {
      // Query keys match by prefix, so this covers the list and every staff
      // member's detail under it — including the card the action came from.
      queryClient.invalidateQueries({ queryKey: ['staff', orgId] })
      showSuccess(successMessage)
    },
  })
}
